import React from 'react';
import {
  Stepper as MuiStepper,
  Step,
  StepLabel,
  StepContent,
  StepButton,
  StepIcon,
  Box,
  Typography,
  Button,
  Collapse,
  LinearProgress,
} from '@mui/material';
import {
  Check,
  Warning,
  Error as ErrorIcon,
  Circle,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledStepper = styled(MuiStepper, {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant?: 'default' | 'outlined' | 'compact' }>(
  ({ theme, variant = 'default' }) => ({
    ...(variant === 'outlined' && {
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
    }),
    ...(variant === 'compact' && {
      '& .MuiStepLabel-root': {
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
      },
    }),
  })
);

const CustomStepIcon = styled('div')<{ 
  ownerState: { 
    active: boolean; 
    completed: boolean; 
    error: boolean;
    icon?: React.ReactNode;
    color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  } 
}>(({ theme, ownerState }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.875rem',
  fontWeight: 500,
  backgroundColor: theme.palette.grey[300],
  color: theme.palette.text.secondary,
  
  ...(ownerState.active && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
  }),
  
  ...(ownerState.completed && {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  }),
  
  ...(ownerState.error && {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  }),

  ...(ownerState.color && ownerState.color !== 'primary' && ownerState.active && {
    backgroundColor: theme.palette[ownerState.color].main,
    color: theme.palette[ownerState.color].contrastText,
    boxShadow: `0 0 0 3px ${theme.palette[ownerState.color].main}20`,
  }),
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

export interface StepData {
  /**
   * Unique identifier for the step
   */
  id: string;
  /**
   * Step label
   */
  label: string;
  /**
   * Optional description
   */
  description?: string;
  /**
   * Step content (for vertical stepper)
   */
  content?: React.ReactNode;
  /**
   * Custom icon for the step
   */
  icon?: React.ReactNode;
  /**
   * Whether the step is optional
   */
  optional?: boolean;
  /**
   * Whether the step has an error
   */
  error?: boolean;
  /**
   * Whether the step is disabled
   */
  disabled?: boolean;
  /**
   * Color variant for the step
   */
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
}

export interface StepperProps {
  /**
   * Array of step data
   */
  steps: StepData[];
  /**
   * Current active step (0-based)
   */
  activeStep: number;
  /**
   * Callback fired when a step is clicked (for non-linear steppers)
   */
  onStepClick?: (step: number) => void;
  /**
   * Stepper orientation
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Visual variant
   */
  variant?: 'default' | 'outlined' | 'compact';
  /**
   * Whether the stepper is non-linear (allows clicking on any step)
   */
  nonLinear?: boolean;
  /**
   * Whether to show progress indicator
   */
  showProgress?: boolean;
  /**
   * Alternative stepper type
   */
  alternativeLabel?: boolean;
  /**
   * Custom connector component
   */
  connector?: React.ReactElement;
  /**
   * Whether to animate step transitions
   */
  animated?: boolean;
}

export interface StepperControlsProps {
  /**
   * Current active step
   */
  activeStep: number;
  /**
   * Total number of steps
   */
  totalSteps: number;
  /**
   * Callback fired when next button is clicked
   */
  onNext?: () => void;
  /**
   * Callback fired when back button is clicked
   */
  onBack?: () => void;
  /**
   * Callback fired when reset button is clicked
   */
  onReset?: () => void;
  /**
   * Whether the next button is disabled
   */
  nextDisabled?: boolean;
  /**
   * Whether the back button is disabled
   */
  backDisabled?: boolean;
  /**
   * Custom labels
   */
  labels?: {
    next?: string;
    back?: string;
    finish?: string;
    reset?: string;
  };
  /**
   * Whether to show reset button when completed
   */
  showReset?: boolean;
}

const defaultLabels = {
  next: 'Next',
  back: 'Back',
  finish: 'Finish',
  reset: 'Reset',
};

const renderStepIcon = (stepData: StepData, index: number, activeStep: number) => {
  const isActive = index === activeStep;
  const isCompleted = index < activeStep;
  const hasError = Boolean(stepData.error);

  if (stepData.icon) {
    return (
      <CustomStepIcon
        ownerState={{
          active: isActive,
          completed: isCompleted,
          error: hasError,
          icon: stepData.icon,
          color: stepData.color,
        }}
      >
        {stepData.icon}
      </CustomStepIcon>
    );
  }

  if (hasError) {
    return (
      <CustomStepIcon
        ownerState={{
          active: isActive,
          completed: isCompleted,
          error: hasError,
          color: stepData.color,
        }}
      >
        <ErrorIcon fontSize="small" />
      </CustomStepIcon>
    );
  }

  if (isCompleted) {
    return (
      <CustomStepIcon
        ownerState={{
          active: isActive,
          completed: isCompleted,
          error: hasError,
          color: stepData.color,
        }}
      >
        <Check fontSize="small" />
      </CustomStepIcon>
    );
  }

  return (
    <CustomStepIcon
      ownerState={{
        active: isActive,
        completed: isCompleted,
        error: hasError,
        color: stepData.color,
      }}
    >
      {index + 1}
    </CustomStepIcon>
  );
};

export const Stepper: React.FC<StepperProps> = ({
  steps,
  activeStep,
  onStepClick,
  orientation = 'horizontal',
  variant = 'default',
  nonLinear = false,
  showProgress = false,
  alternativeLabel = false,
  connector,
  animated = true,
}) => {
  const progress = ((activeStep + 1) / steps.length) * 100;

  const handleStepClick = (stepIndex: number) => {
    if (nonLinear && onStepClick && !steps[stepIndex].disabled) {
      onStepClick(stepIndex);
    }
  };

  const renderStep = (stepData: StepData, index: number) => {
    const isActive = index === activeStep;
    const isCompleted = index < activeStep;
    const stepProps: any = {
      completed: isCompleted,
      disabled: Boolean(stepData.disabled),
    };

    if (stepData.error) {
      stepProps.error = true;
    }

    const labelProps: any = {
      optional: stepData.optional ? (
        <Typography variant="caption" color="textSecondary">
          Optional
        </Typography>
      ) : undefined,
      error: Boolean(stepData.error),
      StepIconComponent: () => renderStepIcon(stepData, index, activeStep),
    };

    if (nonLinear) {
      return (
        <Step key={stepData.id} {...stepProps}>
          <StepButton
            onClick={() => handleStepClick(index)}
            {...labelProps}
          >
            {stepData.label}
            {stepData.description && (
              <Typography variant="body2" color="textSecondary">
                {stepData.description}
              </Typography>
            )}
          </StepButton>
          {orientation === 'vertical' && stepData.content && (
            <StepContent>
              <Box sx={{ pb: 2 }}>
                {stepData.content}
              </Box>
            </StepContent>
          )}
        </Step>
      );
    }

    return (
      <Step key={stepData.id} {...stepProps}>
        <StepLabel {...labelProps}>
          {stepData.label}
          {stepData.description && orientation === 'vertical' && (
            <Typography variant="body2" color="textSecondary">
              {stepData.description}
            </Typography>
          )}
        </StepLabel>
        {orientation === 'vertical' && stepData.content && isActive && (
          <StepContent>
            <Collapse in={isActive} timeout={animated ? 300 : 0}>
              <Box sx={{ pb: 2 }}>
                {stepData.content}
              </Box>
            </Collapse>
          </StepContent>
        )}
      </Step>
    );
  };

  return (
    <Box>
      {showProgress && (
        <ProgressContainer>
          <Typography variant="body2" color="textSecondary" sx={{ minWidth: 60 }}>
            Step {activeStep + 1} of {steps.length}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ flex: 1, height: 6, borderRadius: 3 }}
          />
          <Typography variant="body2" color="textSecondary">
            {Math.round(progress)}%
          </Typography>
        </ProgressContainer>
      )}
      
      <StyledStepper
        activeStep={activeStep}
        orientation={orientation}
        alternativeLabel={alternativeLabel}
        connector={connector}
        nonLinear={nonLinear}
        sx={{
          ...(variant === 'outlined' && {
            padding: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'background.paper',
          }),
          ...(variant === 'compact' && {
            '& .MuiStepLabel-root': {
              paddingTop: 0.5,
              paddingBottom: 0.5,
            },
          }),
        }}
      >
        {steps.map(renderStep)}
      </StyledStepper>
    </Box>
  );
};

export const StepperControls: React.FC<StepperControlsProps> = ({
  activeStep,
  totalSteps,
  onNext,
  onBack,
  onReset,
  nextDisabled = false,
  backDisabled = false,
  labels = defaultLabels,
  showReset = true,
}) => {
  const mergedLabels = { ...defaultLabels, ...labels };
  const isLastStep = activeStep === totalSteps - 1;
  const isCompleted = activeStep >= totalSteps;

  if (isCompleted && showReset) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
        <Button onClick={onReset} variant="outlined">
          {mergedLabels.reset}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
      <Button
        disabled={activeStep === 0 || backDisabled}
        onClick={onBack}
        variant="outlined"
      >
        {mergedLabels.back}
      </Button>
      
      <Button
        disabled={nextDisabled}
        onClick={onNext}
        variant="contained"
      >
        {isLastStep ? mergedLabels.finish : mergedLabels.next}
      </Button>
    </Box>
  );
}; 